import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { finalize } from 'rxjs/operators';
import {
  DEFAULT_PHONE_NUMBER_VALUE,
  OTP_LENGTH,
  PHONE_NUMBER_MASK,
} from '../../../../constants';
import { ExceptionCode } from '@api/models';
import type { OtpConfirmDto, OtpRequestDto, UpdatePhoneNumberRequestDto } from '@api/models';
import { ModalResult } from '../../../../interfaces/modal-result.interface';
import { CustomerService } from '../../../../services/customer.service';
import { ButtonComponent } from '../../../button/button.component';
import { IconComponent } from '../../../icon/icon.component';
import { OtpComponent } from '../../../otp/otp.component';
import { AutofocusDirective } from '../../../../directives/autofocus.directive';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';
import { resolveHttpErrorCode } from '@/helpers/resolve-http-error-code.helper';
import { MergeAccountsComponent } from '../merge-accounts/merge-accounts.component';

@Component({
  selector: 'tc-update-phone-modal',
  templateUrl: './update-phone-modal.component.html',
  styleUrls: ['./update-phone-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    OtpComponent,
    IconComponent,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ButtonComponent,
    AutofocusDirective,
    MergeAccountsComponent,
  ],
})
export class UpdatePhoneModalComponent implements OnInit {
  dialogRef = inject(MatDialogRef<UpdatePhoneModalComponent, ModalResult>);
  private destroyRef = inject(DestroyRef);
  private customerService = inject(CustomerService);

  readonly form = new FormGroup({
    phoneNumber: new FormControl('', [Validators.required, Validators.minLength(5)]),
    otpDigits: new FormArray(
      new Array(OTP_LENGTH).fill(null).map(() => new FormControl('', Validators.required)),
    ),
  });

  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  state = signal<'input' | 'otp' | 'conflict' | 'success'>('input');
  otpRequest = signal<OtpRequestDto>(null);
  isFormErrorsVisible = signal<boolean>(false);
  updateInitResponseMessage = signal<string>(null);
  /** The phone number that turned out to belong to another account, and the message explaining that */
  conflictPhoneNumber = signal<string>(null);
  conflictMessage = signal<string>(null);
  isMerged = signal<boolean>(false);

  ngOnInit(): void {
    this.forcePhoneNumberToDefaultIfApplicable();

    const currentPhone = this.customerService.customer().contactInfo.phoneNumber;
    this.form.controls.phoneNumber.setValue(currentPhone || DEFAULT_PHONE_NUMBER_VALUE);

    this.form.controls.phoneNumber.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.disableClose = true);
  }

  onSubmit(): void {
    this.error.set(null);
    this.isFormErrorsVisible.set(true);

    switch (this.state()) {
      case 'input':
        if (this.form.controls.phoneNumber.invalid) {
          return;
        }

        const phoneNumber = this.form.controls.phoneNumber.getRawValue();
        const currentPhone = this.customerService.customer().contactInfo.phoneNumber;

        if (phoneNumber === currentPhone) {
          return;
        }

        this.updatePhoneNumberInit();
        break;

      case 'otp':
        this.isLoading.set(true);
        const otpConfirmDto: OtpConfirmDto = {
          requestId: this.otpRequest().requestId,
          otpCode: this.form.controls.otpDigits.getRawValue().join(''),
        };

        this.customerService.updatePhoneNumberConfirm(otpConfirmDto)
          .pipe(
            finalize(() => this.isLoading.set(false)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: () => {
              this.state.set('success');
              this.dialogRef.disableClose = false;
            },
            error: error => this.handleError(error),
          });
        break;

      case 'success':
        this.dialogRef.close({ type: 'CONFIRM' });
        break;
    }
  }

  isControlInvalid(control: FormControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  isSubmitDisabled(): boolean {
    if (this.state() !== 'input') {
      return false;
    }

    const phoneNumber = this.form.controls.phoneNumber.getRawValue();
    const currentPhone = this.customerService.customer().contactInfo.phoneNumber || DEFAULT_PHONE_NUMBER_VALUE;
    return phoneNumber === currentPhone;
  }

  updatePhoneNumberInit(): void {
    this.error.set(null);

    this.isLoading.set(true);
    const dto: UpdatePhoneNumberRequestDto = {
      phoneNumber: this.form.controls.phoneNumber.getRawValue(),
    };

    this.customerService.updatePhoneNumberInit(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          if (response.otpRequest) {
            this.otpRequest.set(response.otpRequest);
            this.state.set('otp');
          } else {
            this.updateInitResponseMessage.set(response.message);
            this.state.set('success');
            this.dialogRef.disableClose = false;
          }
        },
        error: error => this.handleError(error),
      });
  }

  goBackFromOtp(): void {
    this.form.controls.otpDigits.reset();
    this.error.set(null);
    this.isFormErrorsVisible.set(false);
    this.state.set('input');
  }

  /**
   * "Already registered" is not a dead end: the number almost always belongs to another account of the
   * same person, created the first time they logged in by email. Offer to combine them instead of
   * leaving them stranded mid-checkout.
   */
  private handleError(error: unknown): void {
    const phoneNumber = this.form.controls.phoneNumber.getRawValue();
    const message = resolveHttpErrorMessage(error as never);

    if (resolveHttpErrorCode(error as never) === ExceptionCode.CUSTOMER_PHONE_NUMBER_ALREADY_EXISTS) {
      this.conflictPhoneNumber.set(phoneNumber);
      this.conflictMessage.set(message);
      this.state.set('conflict');
      return;
    }

    // No code - render the raw message exactly as before, so nothing regresses
    this.error.set(message);
  }

  onMerged(): void {
    this.isMerged.set(true);
    this.state.set('success');
    this.dialogRef.disableClose = false;
  }

  onMergeCancelled(): void {
    this.error.set(this.conflictMessage());
    this.state.set('input');
  }

  private forcePhoneNumberToDefaultIfApplicable(): void {
    this.form.controls.phoneNumber.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value.length < 3) {
          return;
        }
        const validPrefix = DEFAULT_PHONE_NUMBER_VALUE.slice(1);
        for (let i = validPrefix.length - 1; i >= 1; i--) {
          const currentPrefix = validPrefix.slice(i);
          if (value.startsWith(currentPrefix)) {
            const resolvedPrefix = validPrefix.slice(0, i);
            this.form.controls.phoneNumber.setValue(`${resolvedPrefix}${value}`);
            return;
          }
        }
      });
  }

  protected readonly PHONE_NUMBER_MASK = PHONE_NUMBER_MASK;
}
